<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import {
    Drawer,
    DrawerContent,
    DrawerTrigger,
    DrawerOverlay,
    DrawerHeader,
    DrawerClose
} from '@/components/ui/drawer';
import { Card } from '@/components/ui/card';
import { store } from '@/store/store';
import { choosePatient, getParam } from '@/lib/utils';
import { useRouter, useRoute,  } from 'vue-router';
import { XIcon, ArrowRightIcon, PlusIcon } from 'lucide-vue-next';

const router = useRouter();
const route = useRoute();
const open = ref(false);
const members = computed(() => {
    const currentPatientId = route.params.patientId as string;
    return [...store.members].sort((a, b) => {
        if (a.pid === currentPatientId) return -1;
        if (b.pid === currentPatientId) return 1;
        return 0;
    });
});

async function selectPatient (id: string) {
    choosePatient(id);
    console.log(route.params.patientId, id, route.params.patientId === id)
    if (route.name) {
        await router.replace({ name: route.name, params: { domain: getParam(route), patientId: id } });
    }   
    shouldCleanupHistory = false; // don't pop history when navigating
    open.value = false;
    await router.push({ name: 'consent', params: { domain: getParam(route), patientId: id } });
}

// handle browser back button
function handlePopstate() {
    if (open.value) {
        shouldCleanupHistory = false; // mark that history was popped by browser
        open.value = false;
    }
}

// handle escape key
function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && open.value) {
        e.preventDefault();
        open.value = false;
    }
}

// track if we should clean up history
let shouldCleanupHistory = false;

// watch open state to manage history and event listeners
watch(open, (isOpen) => {
    if (isOpen) {
        // push a new history state when opening drawer
        history.pushState({ drawerOpen: true }, '', window.location.href);
        shouldCleanupHistory = true;
        window.addEventListener('popstate', handlePopstate);
        document.addEventListener('keydown', handleKeydown);
    } else {
        // clean up listeners when closing
        window.removeEventListener('popstate', handlePopstate);
        document.removeEventListener('keydown', handleKeydown);
        
        // remove the history entry we added when opening
        // only if drawer was closed without navigation
        if (shouldCleanupHistory && history.state?.drawerOpen === true) {
            history.back();
        }
        shouldCleanupHistory = false;
    }
});

// cleanup on component unmount
onUnmounted(() => {
    window.removeEventListener('popstate', handlePopstate);
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <Drawer v-model:open="open">
        <DrawerTrigger as-child>
            <slot />
        </DrawerTrigger>
        <DrawerOverlay class="backdrop-blur-sm" />
        <DrawerContent class="p-4 pt-0 max-h flex max-h-[85vh] flex-col rounded-t-3xl w-full max-w-md mx-auto">
            <DrawerHeader class="px-0 py-2 flex justify-between items-center mb-4">
                <div class="text-2xl leading-relaxed font-semibold text-gray-700">Who is this visit for?</div>
                <DrawerClose>
                    <span class="sr-only">Close</span>
                    <XIcon class="size-10 translate-x-2 p-2 text-gray-500" />
                </DrawerClose>
            </DrawerHeader>
            <div class="flex flex-col gap-y-4 overflow-y-auto flex-1 -mr-2 pr-2 pb-20">
                <Card 
                    v-for="patient in members" 
                    class="cursor-pointer px-3 py-3 leading-relaxed border-gray-300 rounded-xl" 
                    :class="{ 'bg-gray-100 opacity-80 active:bg-red-100 active:scale-100 active:border-red-200': patient.upcoming_appointment.appID }"
                    @click="patient.upcoming_appointment.appID ? null : selectPatient(patient.pid)"
                >
                    <div class="flex justify-between items-center w-full">
                        <div>
                            <div class="font-semibold text-gray-600">For <span class="font-bold text-gray-900">{{ patient.name + ' ' + patient.family }}</span></div>
                            <div 
                                v-if="patient.upcoming_appointment.appID" 
                                class="mt-1 text-sm text-gray-400 leading-normal"
                            >
                                You cannot book a new appointment until the upcoming one is either completed or cancelled.
                            </div>
                        </div>
                        <ArrowRightIcon v-if="!patient.upcoming_appointment.appID" class="size-5 shrink-0 text-gray-400" />
                    </div>
                </Card>
                <Card class="cursor-pointer px-3 py-3 border-gray-300 rounded-xl" @click="shouldCleanupHistory = false; open = false">
                    <RouterLink to="/add" class="flex justify-between items-center">
                        <div>
                            <PlusIcon class="size-6 shrink-0 text-secondary"/>
                            <div class="font-semibold text-gray-600">Add <span class="font-bold text-gray-900">Someone in My Care</span></div>
                            <div class="mt-1 text-sm text-gray-400">If you are the legal guardian or have the authority to make medical decisions for someone else</div>
                        </div>
                        <ArrowRightIcon class="size-5 shrink-0 text-gray-400" />
                    </RouterLink>
                </Card>
            </div>
        </DrawerContent>
    </Drawer>
</template>
